import React, { useCallback, useEffect, useState } from 'react'
import {
  Flex,
  Text,
  Button,
  ArrowUpDownIcon,
  Box,
  Toggle,
  useModal,
  useMatchBreakpoints,
  Image,
  useTooltip,
  HelpIcon,
} from 'vvs-uikit'
import styled from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import Column, { AutoColumn } from 'components/Layout/Column'
import { CurrencyAmount, Token, Trade, Fraction } from 'vvs-sdk'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import useGelatoLimitOrders from 'state/limitOrders/useGelatoLimitOrders'
import useGasOverhead from 'state/limitOrders/useGasOverhead'
import { ApprovalState, useApproveCallbackFromInputCurrencyAmount } from 'hooks/useApproveCallback'
import useTheme from 'hooks/useTheme'
import { Field, Rate } from 'state/limitOrders/types'
import { useDefaultsFromURLSearch } from 'state/limitOrders/hooks'
import { maxAmountSpend } from 'utils/maxAmountSpend'
import { AutoRow, RowBetween } from 'components/Layout/Row'
import ConnectWalletButton from 'components/ConnectWalletButton'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { GELATO_NATIVE } from 'config/constants'
import getRatePercentageDifference from './utils/getRatePercentageDifference'
import { getRatePercentageMessage, PercentageDirection } from './utils/getRatePercentageMessage'
import ConfirmLimitOrdersModal from './ConfirmLimitOrdersModal'
import TradePrice from '../TradePrice'
import { ArrowWrapper, SwapCallbackError, Wrapper } from '../styleds'

const PercentageButton = styled(Button)`
  border-radius: 50px;
  width: 50px;
  height: 36px;
  padding: 9px 12px;
  margin: 0px 6px;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
`
const Label = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;

  ${Text} {
    margin-left: 8px;
  }
`

const DIRECTION_COLORS = {
  [PercentageDirection.ABOVE]: 'success',
  [PercentageDirection.BELOW]: 'failure',
  [PercentageDirection.MARKET]: 'text',
}

const LimitOrders = () => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const { isMobile } = useMatchBreakpoints()
  const { isDark } = useTheme()
  useDefaultsFromURLSearch()

  const {
    handlers: { handleInput, handleCurrencySelection, handleSwitchTokens, handleLimitOrderSubmission, handleRateType },
    derivedOrderInfo: {
      currencies,
      currencyBalances,
      parsedAmounts,
      formattedAmounts,
      rawAmounts,
      trade,
      price,
      inputError,
      wrappedCurrencies,
      singleTokenPrice,
      currencyIds,
    },
    orderState: { independentField, basisField, rateType },
  } = useGelatoLimitOrders()

  // Trade execution price is always "in MUL mode", even if UI handles DIV rate
  const currentMarketRate = trade?.executionPrice
  const percentageRateDifference = getRatePercentageDifference(
    rateType === Rate.MUL ? currentMarketRate : currentMarketRate?.invert(),
    rateType === Rate.MUL ? price : price?.invert(),
  )
  const [ratePercentageMessage, direction] = getRatePercentageMessage(percentageRateDifference, t, rateType)
  const priceLabelColor = DIRECTION_COLORS[direction]

  const TooltipComponent = () => (
    <>
      <Text color="white" mb="16px">
        {t(
          'Real execution price factors in gas fee necessary to execute your order. It fluctuates according to gas prices.',
        )}
      </Text>
    </>
  )

  const { targetRef, tooltip, tooltipVisible } = useTooltip(<TooltipComponent />, {
    placement: 'bottom-end',
    tooltipOffset: [20, 10],
  })

  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)
  const [approvalState, approveCallback] = useApproveCallbackFromInputCurrencyAmount(parsedAmounts.input)
  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approvalState === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approvalState, approvalSubmitted])
  const showApproveFlow =
    !inputError && (approvalState === ApprovalState.NOT_APPROVED || approvalState === ApprovalState.PENDING)

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances.input)
  const [showInverted, setShowInverted] = useState<boolean>(true)
  const { realExecutionPriceAsString, gasPrice } = useGasOverhead(parsedAmounts.input, parsedAmounts.output, rateType)
  const toggleRateType = () => {
    if ((rateType === Rate.MUL && showInverted) || (rateType !== Rate.MUL && !showInverted)) {
      setShowInverted(!showInverted)
    }
    handleRateType(rateType, price)
    handleTypeInput('')
    handleTypeOutput('')
  }

  const [{ swapErrorMessage, attemptingTxn, txHash, tradeToConfirm }, setSwapState] = useState<{
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined,
  })

  // UI handlers
  const handleTypeInput = useCallback(
    (value: string) => {
      handleInput(Field.INPUT, value)
    },
    [handleInput],
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      handleInput(Field.OUTPUT, value)
    },
    [handleInput],
  )
  const handleInputSelect = useCallback(
    (inputCurrency) => {
      setApprovalSubmitted(false)
      handleCurrencySelection(Field.INPUT, inputCurrency)
    },
    [handleCurrencySelection],
  )
  const handleTypeDesiredRate = useCallback(
    (value: string) => {
      handleInput(Field.PRICE, value)
    },
    [handleInput],
  )
  const handleMaxInput = useCallback(() => {
    if (maxAmountInput) {
      handleInput(Field.INPUT, maxAmountInput.toExact())
    }
  }, [maxAmountInput, handleInput])
  const handleOutputSelect = useCallback(
    (outputCurrency) => {
      handleCurrencySelection(Field.OUTPUT, outputCurrency)
    },
    [handleCurrencySelection],
  )
  const handleApprove = useCallback(async () => {
    await approveCallback()
  }, [approveCallback])

  const handleConfirmDismiss = useCallback(() => {
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      handleTypeInput('')
    }
  }, [txHash, handleTypeInput])

  // get custom setting values for user
  const [allowedSlippage] = useUserSlippageTolerance()

  const handlePlaceOrder = useCallback(() => {
    if (!handleLimitOrderSubmission) {
      return
    }
    setSwapState((prev) => ({
      attemptingTxn: true,
      tradeToConfirm: prev.tradeToConfirm,
      swapErrorMessage: undefined,
      txHash: undefined,
    }))

    try {
      if (!wrappedCurrencies.input?.address) {
        throw new Error('Invalid input currency')
      }
      if (!wrappedCurrencies.output?.address) {
        throw new Error('Invalid output currency')
      }
      if (!rawAmounts.input) {
        throw new Error('Invalid input amount')
      }
      if (!rawAmounts.output) {
        throw new Error('Invalid output amount')
      }

      if (!account) {
        throw new Error('No account')
      }
      const inputToken = currencies.input instanceof Token ? wrappedCurrencies.input?.address : GELATO_NATIVE
      const outputToken = currencies.output instanceof Token ? wrappedCurrencies.output?.address : GELATO_NATIVE

      const orderToSubmit = {
        inputToken,
        outputToken,
        inputAmount: rawAmounts.input,
        outputAmount: rawAmounts.output,
        owner: account,
      }
      handleLimitOrderSubmission(orderToSubmit)
        .then(({ hash }) => {
          setSwapState((prev) => ({
            attemptingTxn: false,
            tradeToConfirm: prev.tradeToConfirm,
            swapErrorMessage: undefined,
            txHash: hash,
          }))
        })
        .catch((error) => {
          setSwapState((prev) => ({
            attemptingTxn: false,
            tradeToConfirm: prev.tradeToConfirm,
            swapErrorMessage: error.message,
            txHash: undefined,
          }))
        })
    } catch (error) {
      console.error(error)
    }
  }, [
    handleLimitOrderSubmission,
    account,
    rawAmounts.input,
    rawAmounts.output,
    currencies.input,
    currencies.output,
    wrappedCurrencies.input?.address,
    wrappedCurrencies.output?.address,
  ])
  const [onPresentConfirmModal] = useModal(
    <ConfirmLimitOrdersModal
      limitPrice={price?.toSignificant(6)}
      currencies={currencies}
      formattedAmounts={formattedAmounts}
      rawAmounts={rawAmounts}
      trade={trade}
      attemptingTxn={attemptingTxn}
      txHash={txHash}
      allowedSlippage={allowedSlippage}
      onConfirm={handlePlaceOrder}
      swapErrorMessage={swapErrorMessage}
      customOnDismiss={handleConfirmDismiss}
    />,
    true,
    true,
    'confirmLimitOrdersModal',
  )

  return (
    <>
      <Wrapper id="limit-orders-page">
        <AutoColumn gap="md">
          <Flex
            mx="10px"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            flexDirection={isMobile ? 'column' : 'row'}
          >
            <Text color="textSubtle">{t('Place a limit order to trade at a set target price')}</Text>
            <Flex alignSelf={isMobile ? 'center' : 'end'} width="35%" marginTop={isMobile ? '10px' : null}>
              <Box
                onClick={() => {
                  if (rateType !== Rate.MUL) return
                  toggleRateType()
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '30px',
                  marginRight: '10px',
                  height: '40px',
                  width: '100%',
                  border: 'solid grey',
                  borderWidth: rateType !== Rate.MUL ? '0px' : '2px',
                  color: rateType !== Rate.MUL ? 'white' : 'black',
                  backgroundColor: rateType !== Rate.MUL ? '#58BD95' : 'white',
                  cursor: 'pointer',
                }}
              >
                <Flex justifyContent="center" alignItems="center">
                  Buy
                </Flex>
              </Box>
              <Box
                onClick={() => {
                  if (rateType === Rate.MUL) return
                  toggleRateType()
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '30px',
                  marginRight: '10px',
                  height: '40px',
                  width: '100%',
                  border: 'solid grey',
                  borderWidth: rateType === Rate.MUL ? '0px' : '2px',
                  color: rateType === Rate.MUL ? 'white' : 'black',
                  backgroundColor: rateType === Rate.MUL ? '#D06969' : 'white',
                  cursor: 'pointer',
                }}
              >
                <Flex justifyContent="center" alignItems="center">
                  Sell
                </Flex>
              </Box>
            </Flex>
          </Flex>
          <CurrencyInputPanel
            label={t('You Pay')}
            value={formattedAmounts[Field.INPUT]}
            showMaxButton={false}
            currency={currencies[Field.INPUT]}
            onUserInput={handleTypeInput}
            onMax={handleMaxInput}
            onCurrencySelect={handleInputSelect}
            otherCurrency={currencies[Field.OUTPUT]}
            id="limit-orders-input"
            showPercentageButtons
          />
          {/* <AutoColumn justify="space-between">
            <AutoRow justify="center" style={{ padding: '0 1rem' }}>
              <PercentageButton onClick={() => handleChangePercent(25)}>25%</PercentageButton>
              <PercentageButton onClick={() => handleChangePercent(50)}>50%</PercentageButton>
              <PercentageButton onClick={() => handleChangePercent(75)}>75%</PercentageButton>
              <PercentageButton onClick={() => handleChangePercent(100)}>MAX</PercentageButton>
            </AutoRow>
          </AutoColumn> */}
          <CurrencyInputPanel
            value={formattedAmounts[Field.PRICE]}
            onUserInput={handleTypeDesiredRate}
            label={currencies[Field.OUTPUT] ? `${currencies[Field.OUTPUT].symbol} Limit Price` : 'Limit Price'}
            showMaxButton={false}
            currency={currencies[Field.OUTPUT]}
            onCurrencySelect={handleOutputSelect}
            otherCurrency={currencies[Field.OUTPUT]}
            id="limit-orders-price"
            disableCurrencySelect
            hideBalance
          />
          <AutoColumn gap="8px">
            {Boolean(trade) && (
              <Flex justifyContent="space-between">
                {tooltipVisible && tooltip}
                <Label>Real Execution Price</Label>
                <Flex>
                  <Text fontSize="14px" mr="5px">
                    {realExecutionPriceAsString}
                  </Text>
                  <Box ref={targetRef}>
                    <HelpIcon color="primary" />
                  </Box>
                </Flex>
              </Flex>
            )}
          </AutoColumn>
          <AutoColumn gap="8px">
            {Boolean(trade) && (
              <RowBetween align="center">
                <Label>{t('Price')}</Label>
                <TradePrice
                  price={trade?.executionPrice}
                  showInverted={showInverted}
                  setShowInverted={setShowInverted}
                />
              </RowBetween>
            )}
          </AutoColumn>
          <AutoColumn gap="8px">
            <Flex justifyContent="end">
              {ratePercentageMessage && ratePercentageMessage !== 'market price' && (
                <Text color={priceLabelColor} fontSize="14px">
                  {ratePercentageMessage}
                </Text>
              )}
            </Flex>
          </AutoColumn>
          <AutoColumn justify="space-between">
            <AutoRow justify="center" style={{ padding: '0 1rem' }}>
              <ArrowWrapper clickable>
                <ArrowUpDownIcon
                  onClick={() => {
                    setApprovalSubmitted(false) // reset 2 step UI for approvals
                    handleTypeInput('')
                    handleTypeOutput('')
                    handleSwitchTokens()
                  }}
                  color={currencies[Field.INPUT] && currencies[Field.OUTPUT] ? 'primary' : 'text'}
                />
              </ArrowWrapper>
            </AutoRow>
          </AutoColumn>
          <CurrencyInputPanel
            value={formattedAmounts[Field.OUTPUT]}
            onUserInput={handleTypeOutput}
            label={independentField === Field.INPUT ? t('You Receive (estimated)') : t('You Receive')}
            showMaxButton={false}
            currency={currencies[Field.OUTPUT]}
            onCurrencySelect={handleOutputSelect}
            otherCurrency={currencies[Field.INPUT]}
            id="limit-orders-output"
          />
          <Box mt="0.25rem">
            {!account ? (
              <ConnectWalletButton width="100%" scale="lg" />
            ) : showApproveFlow ? (
              <Button
                variant="primary"
                onClick={handleApprove}
                id="enable-order-button"
                width="100%"
                scale="lg"
                disabled={approvalSubmitted}
              >
                {approvalSubmitted
                  ? t('Enabling %asset%', { asset: currencies.input?.symbol })
                  : t('Enable %asset%', { asset: currencies.input?.symbol })}
              </Button>
            ) : (
              <Button
                variant="primary"
                scale="lg"
                onClick={() => {
                  setSwapState({
                    tradeToConfirm: trade,
                    attemptingTxn: false,
                    swapErrorMessage: undefined,
                    txHash: undefined,
                  })
                  onPresentConfirmModal()
                }}
                id="place-order-button"
                width="100%"
                disabled={!!inputError || realExecutionPriceAsString === 'never executes'}
              >
                {inputError || realExecutionPriceAsString === 'never executes'
                  ? inputError || t("Can't execute this order")
                  : t('Place Limit Order')}
              </Button>
            )}
          </Box>
          <Flex my="5px" alignItems="center" justifyContent="center">
            <Text color="text" mr="5px">
              Powered by
            </Text>
            <Text bold color="text" mr={!isDark ? '5px' : '0px'}>
              Gelato
            </Text>
            <Image
              height={isDark ? 45 : 25}
              width={isDark ? 40 : 20}
              src={isDark ? '/images/gelatoLogo-white.svg' : '/images/gelatoLogo.svg'}
              alt="Gelato Logo"
            />
          </Flex>
        </AutoColumn>
      </Wrapper>
    </>
  )
}

export default LimitOrders
